// Reading a teacher's Google Classroom roster, in the browser.
//
// Google Identity Services gives the page a short-lived access token after the
// teacher picks their Google account and agrees; the page then reads their
// courses and each course's students straight from the Classroom API. Nothing
// from Google is stored: the names go to the roster import, which makes our own
// username accounts from them, and the token is dropped with the page.
//
// Scopes are read-only, and do NOT include student email addresses: names are
// all the import needs.
//
// Needs VITE_GOOGLE_CLIENT_ID: a Google Cloud OAuth client (type "Web
// application") with https://app.gibberfish.app as an authorised JavaScript
// origin, and the Google Classroom API enabled in the same project. The client
// ID is public; there is no client secret in this flow.

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
const SCOPES = [
  'https://www.googleapis.com/auth/classroom.courses.readonly',
  'https://www.googleapis.com/auth/classroom.rosters.readonly',
].join(' ');
const API = 'https://classroom.googleapis.com/v1';

export const googleConfigured = () => !!CLIENT_ID;

export type GCourse = { id: string; name: string; section?: string };

type TokenResponse = { access_token?: string; error?: string; error_description?: string };
type TokenClient = { requestAccessToken: (o?: { prompt?: string }) => void };
type Gis = {
  accounts: { oauth2: { initTokenClient: (c: {
    client_id: string; scope: string; callback: (r: TokenResponse) => void;
    error_callback?: (e: { type?: string; message?: string }) => void;
  }) => TokenClient } };
};

let gisLoading: Promise<Gis> | null = null;
function loadGis(): Promise<Gis> {
  const w = window as unknown as { google?: Gis };
  if (w.google?.accounts?.oauth2) return Promise.resolve(w.google);
  gisLoading ??= new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.onload = () => (w.google ? resolve(w.google) : reject(new Error('Google sign-in did not load.')));
    s.onerror = () => { gisLoading = null; reject(new Error('Could not reach Google. Check your connection.')); };
    document.head.appendChild(s);
  });
  return gisLoading;
}

/** Ask the teacher to let us read their Classroom courses and rosters. */
export async function connectGoogle(): Promise<string> {
  if (!CLIENT_ID) throw new Error('Google Classroom import is not set up yet.');
  const gis = await loadGis();
  return new Promise((resolve, reject) => {
    const client = gis.accounts.oauth2.initTokenClient({
      client_id: CLIENT_ID,
      scope: SCOPES,
      callback: r => (r.access_token ? resolve(r.access_token)
        : reject(new Error(r.error_description || 'Google did not give access.'))),
      error_callback: e => reject(new Error(e.type === 'popup_closed' ? 'The Google window was closed.' : e.message || 'Google sign-in failed.')),
    });
    client.requestAccessToken({ prompt: 'select_account' });
  });
}

async function get<T>(token: string, path: string): Promise<T> {
  const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (res.status === 401) throw new Error('Google access ran out. Connect again.');
  if (res.status === 403) throw new Error('Google refused: this account may not be allowed to use Classroom, or access was not granted.');
  if (!res.ok) throw new Error(`Google Classroom error (${res.status}).`);
  return res.json() as Promise<T>;
}

/** The courses this account teaches that are still running. */
export async function listCourses(token: string): Promise<GCourse[]> {
  const out: GCourse[] = [];
  let page = '';
  do {
    const r = await get<{ courses?: GCourse[]; nextPageToken?: string }>(
      token, `/courses?teacherId=me&courseStates=ACTIVE&pageSize=100${page ? `&pageToken=${encodeURIComponent(page)}` : ''}`,
    );
    out.push(...(r.courses ?? []).map(c => ({ id: c.id, name: c.name, section: c.section })));
    page = r.nextPageToken ?? '';
  } while (page);
  return out;
}

/** Every student's full name in one course. */
export async function listStudents(token: string, courseId: string): Promise<string[]> {
  const out: string[] = [];
  let page = '';
  do {
    const r = await get<{ students?: { profile?: { name?: { fullName?: string } } }[]; nextPageToken?: string }>(
      token, `/courses/${encodeURIComponent(courseId)}/students?pageSize=100${page ? `&pageToken=${encodeURIComponent(page)}` : ''}`,
    );
    for (const s of r.students ?? []) {
      const n = s.profile?.name?.fullName?.trim();
      if (n) out.push(n);
    }
    page = r.nextPageToken ?? '';
  } while (page);
  return out;
}
